/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 26/12/2024 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 11/06/2026 | Lic. José Luis Aragón Cervantes  | Clave de cliente antes de razón social en datos del cliente
* --------------------------------------------------------------------------------------- 
 *      3       | 26/06/2026 | Lic. Jose Luis Aragon Cervantes  | Integracion autonoma y referencias portatiles
*/

import TextField from "../../ui/TextField";
import Autocomplete from "../../ui/Autocomplete";
import React, { Fragment, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { apiValidarCostosDescuento } from "../../../settings/ApiConfig";
import { Box, Typography } from "@mui/material";
import { isNullOrEmpty } from "../../../settings/utils";
import { useVentas } from "../../../context/Ventas/VentasContext";
import MnuAplicarDescuento from "../../ventas/MnuAplicarDescuento";
import SizeSoftXVentasLayout from "../../leyout/SizeSoftXVentasLayout";
import SizeSoftXTableVentas from "../../leyout/SizeSoftXTableVentas";
import { useRequest } from "../../../context/System/RequestContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useInventarios } from "../../../context/Inventarios/InventariosContext";
import SizeSoftXModalPortatil from "../leyout/SizeSoftXModalPortatil";

const OPCIONES_DESCUENTO = [
  { label: "5%", value: 0.05 },
  { label: "7%", value: 0.07 },
  { label: "10%", value: 0.10 },
  { label: "12%", value: 0.12 },
];

export default function ModalAplicacionDescuentos(props) {
  const { openModal, handleCloseModalCambioCosto, data, dataCliente } = props;

  const { handleOpenAlert } = useLayout();
  const { GetRequest } = useRequest();
  const { handleObtenerArticuloDataFull } = useInventarios();

  const {
    handleAplicarDescuentosManual,
    setStatusKeyActions,
    handleAplicarDescuentosVenta,
  } = useVentas();

  // -------------------------
  // State
  // -------------------------
  const [draft, setDraft] = useState({});
  const [articuloSelect, setArticuloSelect] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [listaVenta, setListaVenta] = useState([]);
  const [descuentoGeneralSelec, setDescuentoGeneralSelec] = useState(null);

  // refs para evitar closures viejas y carreras
  const listaVentaRef = useRef([]);
  useEffect(() => {
    listaVentaRef.current = listaVenta;
  }, [listaVenta]);

  // lock para no ejecutar doble commit por Enter
  const commitLockRef = useRef(new Set()); // keys tipo "id-field"

  // -------------------------
  // Input refs + foco
  // -------------------------
  const inputRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const setRef = useCallback(
    (idPartida, col) => (el) => {
      if (!el) return;
      inputRefs.current[`${idPartida}-${col}`] = el;
    },
    []
  );

  const didInitFocusRef = useRef(false);

  const focusCell = useCallback((idPartida, col) => {
    let tries = 0;
    const maxTries = 25;

    const tick = () => {
      tries++;
      const el = inputRefs.current[`${idPartida}-${col}`];
      if (el?.focus) {
        el.focus();
        el.select?.();
        return;
      }
      if (tries < maxTries) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, []);

  // -------------------------
  // Helpers
  // -------------------------
  const round = (value, decimals = 0) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    const factor = 10 ** decimals;
    return Math.round((n + Number.EPSILON) * factor) / factor;
  };
  const roundMoney = (value) => round(value, 2);

  const totales = useMemo(() => {
    const list = Array.isArray(listaVenta) ? listaVenta : [];
    return {
      subtotal: roundMoney(list.reduce((s, i) => s + (Number(i.subtotal) || 0), 0)),
      iva:      roundMoney(list.reduce((s, i) => s + (Number(i.iva) || 0), 0)),
      descuento:roundMoney(list.reduce((s, i) => s + (Number(i.descuento) || 0), 0)),
      total:    roundMoney(list.reduce((s, i) => s + (Number(i.total) || 0), 0)),
    };
  }, [listaVenta]);

  const totalesBase = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    let subtotal = 0, iva = 0, total = 0;
    list.forEach(item => {
      const qty = Number(item.cantidad) || 0;
      const P0 = Number(item.preciodefinitivo) || Number(item.costounitario) || 0;
      const tieneIVA = (item.ivaventa ?? "").trim().toUpperCase() === "SI";
      const r = Number(item.porcentajeiva) || 0;
      const t = P0 * qty;
      if (tieneIVA) {
        const baseUnit = P0 / (1 + r);
        subtotal += baseUnit * qty;
        iva += (P0 - baseUnit) * qty;
      } else {
        subtotal += t;
      }
      total += t;
    });
    return {
      subtotal: roundMoney(subtotal),
      iva: roundMoney(iva),
      total: roundMoney(total),
    };
  }, [data]);

  const same4 = (a, b) => round(Number(a) || 0, 4) === round(Number(b) || 0, 4);

  const k = (id, field) => `${id}-${field}`;

  const getDraftValue = (row, field) => {
    const key = k(row.idpartida, field);
    return draft[key] ?? String(row[field] ?? "");
  };

  const setDraftValue = (id, field, value) => {
    setDraft((prev) => ({ ...prev, [k(id, field)]: value }));
  };

  const clearDraftValue = (id, field) => {
    setDraft((prev) => {
      const copy = { ...prev };
      delete copy[k(id, field)];
      return copy;
    });
  };

  const parseNumber = (rawValue) => {
    const raw = String(rawValue ?? "").trim();
    if (raw === "") return 0;
    const normalized = raw.replaceAll(",", ".");
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  // -------------------------
  // Cálculo (una sola lógica consistente)
  // Reglas de negocio:
  // - P0 = precio definitivo ORIGINAL (incluye IVA si ivaventa=SI, si NO entonces no incluye)
  // - Precio pretendido (P) siempre es PRECIO FINAL UNITARIO (mismo tipo que P0)
  // - PMVB valida P; si lo sube, se recalcula % real contra P0
  // -------------------------
  const calcMontosFromPrecioFinalUnit = (articulo, P, qty) => {
    const P0 = Number(articulo?.preciodefinitivo) || 0;
    const precio = Number(P) || 0;
    const q = Number(qty) || 0;

    const tieneIVA = (articulo?.ivaventa ?? "").trim().toUpperCase() === "SI";
    const r = Number(articulo?.porcentajeiva) || 0;

    let subtotal = 0,
      iva = 0,
      total = 0;

    total = precio * q;

    if (tieneIVA) {
      const baseUnit = precio / (1 + r);
      const ivaUnit = precio - baseUnit;
      subtotal = baseUnit * q;
      iva = ivaUnit * q;
    } else {
      subtotal = total;
      iva = 0;
    }

    const descuento = (P0 - precio) * q;
    const porcentaje = P0 > 0 ? 1 - precio / P0 : 0;

    return { subtotal, iva, total, descuento, porcentaje, costocliente: precio };
  };

  // -------------------------
  // API: Validar costo
  // -------------------------
  const getValidarCosto = useCallback(
    async (clavearticulo, medida, costo) => {
      if (isNullOrEmpty(clavearticulo)) return 0;
      if (!medida || medida === 0) return 0;
      if (!costo || costo === 0) return 0;

      const response = await GetRequest({
        url: apiValidarCostosDescuento
          .replace("{clavearticulo}", String(clavearticulo).trim())
          .replace("{medida}", medida)
          .replace("{costo}", costo),
        loader: "Validando costo descuento",
      });

      if (response?.hasError) return 0;
      return response?.result;
    },
    [GetRequest]
  );

  // Función para validar precio con PMVB
  const aplicarPMVB = async (venta, articulo, precioPretendidoUnit) => {
    const P0 = Number(articulo?.preciodefinitivo) || 0;
    const PMVB = await getValidarCosto(venta.clavearticulo, venta.medida, precioPretendidoUnit);
    const Pmin = (Number(PMVB) || 0) > 0 ? Number(PMVB) : 0;
    const Pfinal = Pmin > 0 ? Pmin : precioPretendidoUnit;
    const porcentajereal = P0 > 0 ? 1 - Pfinal / P0 : 0;
    return { Pfinal, porcentajereal, P0 };
  };

  // -------------------------
  // Aplicar Descuento General
  // -------------------------
  const aplicarDescuentoGeneral = useCallback(async (descuentoDecimal, lista) => {
    const aplicarAArticulo = async (articulo) => {
      const { idpartida, clavearticulo, medida, cantidad } = articulo;
      const qty = Number(cantidad) || 0;

      if (qty <= 0) return articulo;

      const articuloCompleto = await handleObtenerArticuloDataFull(clavearticulo, medida);
      if (!articuloCompleto) return articulo;

      const P0 = Number(articuloCompleto?.preciodefinitivo) || 0;
      if (P0 <= 0) return articulo;

      const Ppretendido = P0 * (1 - descuentoDecimal);
      const PMVB = await getValidarCosto(clavearticulo, medida, Ppretendido);
      const Pmin = Number(PMVB) || 0;
      const Pfinal = Pmin > 0 ? Pmin : Ppretendido;

      const montos = calcMontosFromPrecioFinalUnit(articuloCompleto, Pfinal, qty);

      return {
        ...articulo,
        costocliente: roundMoney(montos.costocliente),
        subtotal: roundMoney(montos.subtotal),
        descuento: roundMoney(montos.descuento),
        iva: round(montos.iva, 2),
        total: roundMoney(montos.total),
        porcentajedescuento: round(montos.porcentaje, 4),
      };
    };

    return await Promise.all(lista.map(aplicarAArticulo));
  }, [handleObtenerArticuloDataFull, getValidarCosto, calcMontosFromPrecioFinalUnit]);

  const handleChangeDescuentoGeneral = async (e, newValue) => {
    setDescuentoGeneralSelec(newValue);
    if (newValue && newValue.value > 0) {
      const listToUpdate = Array.isArray(listaVentaRef.current) ? listaVentaRef.current : [];
      if (listToUpdate.length > 0) {
        const actualizados = await aplicarDescuentoGeneral(newValue.value, listToUpdate);
        setListaVenta(actualizados);
        handleAplicarDescuentosVenta(actualizados);
      }
    }
  };

  // -------------------------
  // Modal open/close + init data (SIN recargar por cada cambio de data)
  // -------------------------
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (openModal) {
      setInternalOpen(true);
      setTimeout(() => {
        setModalVisible(true);
        setStatusKeyActions(true);
      }, 50);
    } else {
      setModalVisible(false);
      setStatusKeyActions(false);
      setTimeout(() => setInternalOpen(false), 500);
    }
  }, [openModal, setStatusKeyActions]);

  // inicializa solo al ABRIR (transición false -> true)
  useEffect(() => {
    const prev = prevOpenRef.current;
    prevOpenRef.current = openModal;

if (!prev && openModal) {
      didInitFocusRef.current = false;
      const initial = Array.isArray(data) ? data.map((x) => ({ ...x })) : [];
      setListaVenta(initial);
      setDraft({});
      setArticuloSelect(initial.length ? initial[0] : null);
      commitLockRef.current.clear();
      setDescuentoGeneralSelec(null);

      // Aplicar porcentaje del cliente si existe
      const descuentoCliente = dataCliente?.porcentajedescuento ?? 0;

      if (descuentoCliente > 0 && initial.length > 0) {
        aplicarDescuentoGeneral(descuentoCliente, initial).then((actualizados) => {
          setListaVenta(actualizados);
          handleAplicarDescuentosVenta(actualizados);
        });
      }
    }
  }, [openModal, data, dataCliente, getValidarCosto, calcMontosFromPrecioFinalUnit, handleAplicarDescuentosVenta, aplicarDescuentoGeneral]);

  // foco inicial
  useEffect(() => {
    if (!openModal || !internalOpen || !modalVisible) return;
    if (!Array.isArray(listaVenta) || listaVenta.length === 0) return;

    const primera = listaVenta[0];

    let tries = 0;
    const maxTries = 15;

    const tick = () => {
      tries++;

      setArticuloSelect(primera);

      const key = `${primera.idpartida}-porcentajedescuento`;
      const el = inputRefs.current[key];

      if (el?.focus) {
        el.focus();
        el.select?.();
        return;
      }

      if (tries < maxTries) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [openModal, internalOpen, modalVisible]);

  // -------------------------
  // Commit
  // -------------------------
  const handleCommitCell = useCallback(
    async (idPartida, field, rawValue) => {
      const lockKey = `${idPartida}-${field}`;
      if (commitLockRef.current.has(lockKey)) return;
      commitLockRef.current.add(lockKey);

      try {
        let val = parseNumber(rawValue);

        const currentList = Array.isArray(listaVentaRef.current) ? listaVentaRef.current : [];
        const venta = currentList.find((v) => v.idpartida === idPartida);
        if (!venta) return;

        const articulo = await handleObtenerArticuloDataFull(venta.clavearticulo, venta.medida);
        if (!articulo) return;

        const qty = Number(venta.cantidad) || 0;
        const P0 = Number(articulo.preciodefinitivo) || 0;

        //solo para subtotal por que se carga el monto total de la cantidad de piezas
        if (field === "subtotal") val = val / qty 

        let Ppretendido = 0;

        if (field === "porcentajedescuento") {
          // val es porcentaje (ej 0.10)
          Ppretendido = P0 * (1 - val);
        } else if (field === "subtotal") {
          // val es PRECIO FINAL UNITARIO (aunque el campo se llame "subtotal")
          Ppretendido = val;
        } else {
          return;
        }

        const { Pfinal } = await aplicarPMVB(venta, articulo, Ppretendido);
        const montos = calcMontosFromPrecioFinalUnit(articulo, Pfinal, qty);

        // Redondeos
        const porcentajedescuento = round(montos.porcentaje, 4);
        const costocliente = roundMoney(montos.costocliente);
        const subtotal = roundMoney(montos.subtotal);
        const descuento = roundMoney(montos.descuento);
        const total = roundMoney(montos.total);
        const iva = round(montos.iva, 2);

        setListaVenta((prev) => {
          const base = Array.isArray(prev) ? prev : [];
          const next = base.map((v) =>
            v.idpartida !== idPartida
              ? v
              : {
                  ...v,
                  costocliente,
                  subtotal,
                  descuento,
                  iva,
                  total,
                  porcentajedescuento,
                }
          );

          // Sincroniza con padre (mismo array)
          handleAplicarDescuentosVenta(next);

          return next;
        });
      } catch (err) {
        console.error(err);
        handleOpenAlert?.("Error al aplicar descuento");
      } finally {
        commitLockRef.current.delete(lockKey);
      }
    },
    [getValidarCosto, handleAplicarDescuentosVenta, handleObtenerArticuloDataFull, handleOpenAlert]
  );

  // -------------------------
  // Selección fila
  // -------------------------
  const handleChangePartidaSeleccionada = (row) => setArticuloSelect(row);

  // -------------------------
  // Navegación/Commit SOLO con Enter
  // -------------------------
  const getNextRow = useCallback((currentId) => {
    
    const list = Array.isArray(listaVentaRef.current) ? listaVentaRef.current : [];
    if (!list.length) return null;

    const cur = currentId;

    // (opcional) asegura orden estable por idpartida
    const ordered = [...list].sort((a, b) => Number(a.idpartida) - Number(b.idpartida));

    let filax = 0;
    const idx = ordered.findIndex(v => v.idpartida === cur);
    if(idx === 0)
    {
      filax = filax + 1;
    }
    else
    {
        filax = (list.length -1) > idx ? idx + 1 : 0;
    }
    return ordered[filax];
  }, []);
  
  const goNextRow = useCallback((currentId) => {
    
    const list = Array.isArray(listaVentaRef.current) ? listaVentaRef.current : [];
    if (!list.length) return null;

    const idx = list.findIndex((v) => v.idpartida === currentId);
    const next = idx >= 0 ? list[idx + 1] : null;
    return next ?? list[0];
  }, []);


  const handleKeyDownCell = useCallback(
    async (e, field, row) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        clearDraftValue(row.idpartida, field);
        return;
      }

      // Solo Enter comitea
      if (e.key !== "Enter") return;

        e.preventDefault();
        e.stopPropagation();

        const value = e.target.value;
        clearDraftValue(row.idpartida, field);
        
        setTimeout(async () => {
          await handleCommitCell(row.idpartida, field, value);
          
          const nextRow = getNextRow(row.idpartida);
          if (!nextRow) return;
          setArticuloSelect(nextRow);
          await focusCell(nextRow.idpartida, field);
        }, 150);
        
        return;
      },
    [clearDraftValue, handleCommitCell, getNextRow, focusCell]
  );

  // -------------------------
  // Atajo global (Alt+A) - aplica a todo el modal + scroll con flechas
  // -------------------------
  useEffect(() => {
    if (!internalOpen || !modalVisible) return;

    const handleKeyDown = (event) => {
      if (event.altKey && event.key.trim().toUpperCase() === "A") {
        event.preventDefault();
        event.stopPropagation();
        handleAplicarDescuentosManual();
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const active = document.activeElement;
        if (active?.tagName === "INPUT" || active?.tagName === "TEXTAREA" || active?.contentEditable === "true") {
          return;
        }
        
        const container = scrollContainerRef.current;
        if (!container) return;
        
        event.preventDefault();
        event.stopPropagation();
        
        const scrollAmount = 50;
        const isDown = event.key === "ArrowDown";
        
        if (isDown) {
          container.scrollBy({ top: scrollAmount, behavior: "smooth" });
        } else {
          container.scrollBy({ top: -scrollAmount, behavior: "smooth" });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [internalOpen, modalVisible, handleAplicarDescuentosManual]);

  // -------------------------
  // Render
  // -------------------------
  return (
    <SizeSoftXModalPortatil
      open={internalOpen}
      onClose={handleCloseModalCambioCosto}
      aria-labelledby="menu-modal-title"
      aria-describedby="menu-modal-description"
    >
      <Fragment>
        <SizeSoftXVentasLayout titlePage="Formulario de descuentos" buttonActions={<MnuAplicarDescuento />}>
          {dataCliente && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              border: '1px solid #ccc', 
              borderRadius: '4px', 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              gap: '16px',
              marginBottom: '12px'
            }}>
              <div style={{ minWidth: '100px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>CLAVE</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  {dataCliente.clavecliente || '-'}
                </div>
              </div>
              <div style={{ minWidth: '120px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>CLIENTE</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1976d2' }}>
                  {dataCliente.razonsocial || '-'}
                </div>
              </div>
              <div style={{ minWidth: '80px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>DESCUENTO</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2e7d32' }}>
                  {dataCliente.porcentajedescuento || 0}%
                </div>
              </div>
              <div style={{ minWidth: '150px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>CONTACTOS</div>
                <div style={{ fontSize: '12px' }}>
                  1) {dataCliente.contacto || '-'}
                </div>
                <div style={{ fontSize: '12px' }}>
                </div>
              </div>
              <div style={{ minWidth: '150px' }}>
                <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>ACUERDOS</div>
                <div style={{ fontSize: '12px' }}>
                  {dataCliente.acuerdos || '-'}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', marginTop: '8px' }}>
            <Autocomplete
              disableClearable
              id="combo-descuento-general"
              options={OPCIONES_DESCUENTO}
              getOptionLabel={(option) => option.label}
              value={descuentoGeneralSelec}
              onChange={handleChangeDescuentoGeneral}
              renderInput={(params) => (
                <TextField {...params} label="Descuento General" variant="outlined" size="small" />
              )}
              sx={{ width: '220px' }}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
            />
          </div>

          <div className="tabla-descuentos" ref={scrollContainerRef} style={{ maxHeight: 'calc(100vh - 395px)', overflowY: 'auto', overflowX: 'auto' }}>
            <SizeSoftXTableVentas
              columns={[
                { title: "Descripción", field: "descripcion" },
                { title: "Medida", field: "medida" },
                { title: "Cantidad", field: "cantidad" },
                { title: "Consto venta", field: "costounitario" },
                {
                  title: "Desc %",
                  field: "porcentajedescuento",
                  render: (row) => (
                    <TextField
                      name="porcentajedescuento"
                      value={getDraftValue(row, "porcentajedescuento")}
                      onChange={(e) => setDraftValue(row.idpartida, "porcentajedescuento", e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputRef={setRef(row.idpartida, "porcentajedescuento")}
                      onKeyDown={(e) => handleKeyDownCell(e, "porcentajedescuento", row)}
                      autoComplete="off"
                      type="text"
                      sx={{ backgroundColor: "white", width: "100px" }}
                    />
                  ),
                },
                { title: "Descuento", field: "descuento" },
                {
                  title: "Subtotal (Precio unitario)",
                  field: "subtotal",
                  render: (row) => (
                    <TextField
                      name="subtotal"
                      value={getDraftValue(row, "subtotal")}
                      onChange={(e) => setDraftValue(row.idpartida, "subtotal", e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputRef={setRef(row.idpartida, "subtotal")}
                      onKeyDown={(e) => handleKeyDownCell(e, "subtotal", row)}
                      autoComplete="off"
                      type="text"
                      sx={{ backgroundColor: "white", width: "150px" }}
                    />
                  ),
                },
                { title: "IVA", field: "iva" },
                { title: "Total", field: "total" },
              ]}
              data={Array.isArray(listaVenta) ? listaVenta : []}
              id="idpartida"
              row={articuloSelect}
              setRow={handleChangePartidaSeleccionada}
              fontSize={"small"}
              rowSpacing={5}
              rowsPerPageIntro={50}
              disablePagination={true}
            />
          </div>
          <Box sx={{
            display: 'flex', justifyContent: 'space-between',
            padding: '10px 16px', borderTop: '2px solid #1976d2',
            marginTop: '4px', backgroundColor: '#f8f9fa', borderRadius: '4px',
          }}>
            <Box sx={{ display: 'flex', gap: '24px' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Subtotal (Sin desc.)</Typography>
                <Typography variant="body2" fontWeight={600}></Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">IVA (Sin desc.)</Typography>
                <Typography variant="body2" fontWeight={600}></Typography>
              </Box>
              <Box sx={{ textAlign: 'right', borderRight: '1px solid #ccc', pr: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total (Sin desc.)</Typography>
                <Typography variant="body1" fontWeight={700}></Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '24px' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2" fontWeight={600}></Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">IVA</Typography>
                <Typography variant="body2" fontWeight={600}></Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Descuento</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main"></Typography>
              </Box>
              <Box sx={{ textAlign: 'right', borderLeft: '1px solid #ccc', pl: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total</Typography>
                <Typography variant="body1" fontWeight={700} color="primary"></Typography>
              </Box>
            </Box>
          </Box>
        </SizeSoftXVentasLayout>
      </Fragment>
    </SizeSoftXModalPortatil>
  );
}
